import type { ReactNode } from 'react';
import Unit from '../Modular/Unit';
import MicroUnit from '../Modular/MicroUnit';
import type {
  As as UnitElement,
  UnitSettings,
} from '../Modular/ModularTypes/UnitSettings';
import './Input.css';

export type InputElement = Extract<UnitElement, 'input' | 'textarea'>;

export type InputVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'auth';

export type InputProps<T extends InputElement = 'input'> = {
  as?: T;
  id: string;
  label?: ReactNode;
  variant?: InputVariant;
  className?: string;
  fieldClassName?: string;
} & Omit<UnitSettings<T>, 'as' | 'children' | 'className' | 'id'>;

const Input = <T extends InputElement = 'input'>({
  as,
  id,
  label,
  variant = 'primary',
  className = '',
  fieldClassName = '',
  ...props
}: InputProps<T>) => {
  const Component = (as ?? 'input') as T;
  const unitProps = props as UnitSettings<T>;

  return (
    <Unit layout="flex" gap="sm" className={fieldClassName}>
      {label && (
        <MicroUnit
          as="label"
          labelTypography="label"
          labelColor="text-light"
          htmlFor={id}
        >
          {label}
        </MicroUnit>
      )}
      <Unit<T>
        {...unitProps}
        as={Component}
        id={id}
        className={`input-base input-${variant} ${className}`.trim()}
      />
    </Unit>
  );
};

export default Input;
